import Head from "next/head";
import Link from "next/link";
import dynamic from "next/dynamic";
import Theme from "../../components/Theme";
const People = dynamic(() =>
  import("../../components/organisms/common/People")
);
const WideCTA = dynamic(() =>
  import("../../components/organisms/common/WideCTA")
);
const Masonry = dynamic(() =>
  import("../../components/organisms/common/Masonry")
);
const TpWidget = dynamic(() =>
  import("../../components/atoms/document/TpWidget")
);
const CTA = dynamic(() => import("../../components/organisms/common/CTA"));
import AdaptiveCriticalImage from "../../components/atoms/AdaptiveCriticalImage";
import AppStorage from "../../components/AppStorage";
import {
  viewport,
  colors,
  font,
  lineHeight,
} from "../../styles/cssinjs/helpers/variables.js";
import TpWidgetMicro from "@components/organisms/common/TpWidgetMicro";

export async function getStaticProps(ctx) {
  const { locale } = ctx;
  let allData = {};

  // Fetch
  const headerResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/common/header.json`
  );
  const footerResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/common/footer.json`
  );
  const contentResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/features/home.json`
  );

  // Catch Error
  if (contentResponse.status === 404) {
    return {
      notFound: true,
    };
  }

  // Convert
  allData.header = await headerResponse.json();
  allData.footer = await footerResponse.json();
  allData.content = await contentResponse.json();

  // Wrap
  const header = {
    header: allData.header,
  };
  const footer = {
    footer: allData.footer,
  };

  //  Combine all data
  const data = await { ...allData.content, ...header, ...footer };

  return {
    props: {
      data,
      lang: locale,
    },
  };
}
export default function Index({ data, lang, nonce }) {
  const seo = data.page_meta;
  const content = data.page_content;
  const header = data.header;
  const footer = data.footer;
  const FeaturesList = ({ id, order, data, classes }) => {
    let cardClasses;
    // lg:mt-5 for margin top
    // md:mt-2.5 only for first card
    if (data.columnCount === 4) {
      cardClasses = "has-four-col";
    }
    if (data.columnCount === 3) {
      cardClasses = "has-three-col";
    }
    const Lists = ({ data, cols }) => {
      let offsetForSecondRow = "";
      let offsetForFirstCard = "";
      return data.map((item, count, items) => {
        if (count === 1) {
          offsetForFirstCard = "md:mt-2.5";
        }
        if (cols === 3 && count > 2) {
          offsetForSecondRow = "lg:mt-5";
        }
        if (cols === 4 && count > 3) {
          offsetForSecondRow = "lg:mt-5";
        }
        return item.hasOwnProperty("isDummy") ? (
          <div
            key={count}
            style={{ visibility: "hidden" }}
            data-usecase="Dummy element to align with the grid system"
          ></div>
        ) : (
          <Link
            href={item.href}
            key={count}
            prefetch={false}
            className={`btn-outline md:mb-0 md:mt-5 md:pb-3 md:pl-2.5 md:pr-2 md:pt-2 pb-4 pl-3 pr-2 pt-3 text-left ${offsetForFirstCard} ${offsetForSecondRow}`}
            id={item.id}
          >
            <div className="icon-title-wrap">
              <figure dangerouslySetInnerHTML={{ __html: item.icon }}></figure>
              <h3 className="lg:mt-2 lg:mb-3 text-brandcolor">
                {item.heading}
              </h3>
            </div>
            <p>{item.desc}</p>
          </Link>
        );
      });
    };
    return (
      <section className={`bg-light-blue-grey lg:pt-10 ${classes}`} id={id}>
        <article className="container lg:maxw-xl lg:text-center md:text-left">
          <h2 className="lg:mb-3 md:mb-2 text-lg">{data.heading}</h2>
          <p className="mx-5 lg:mb-10 md:m-0">{data.desc}</p>
        </article>
        <div className={`container card--default card--feature ${cardClasses}`}>
          <Lists data={data.lists} cols={data.columnCount} />
        </div>
        {order === "five" ? <AllIntegrationSection /> : ""}
      </section>
    );
  };
  const AllIntegrationSection = () => {
    return (
      <div className="premium-bg-cta br-8 container cta-contrast has-image lg:d-fx lg:mb-20 lg:mt-20 md:pl-0 md:mt-10">
        <article className="lg:maxw-md lg:mr-5 md:pl-2.5">
          <h2 className="lg:mt-1 md:mb-2 text-black text-lg">
            {content.exploreAllIntegrations.title}
          </h2>
          <p className="lg:mb-4.5 lg:mt-3 md:mb-2.5 text-grey">
            {content.exploreAllIntegrations.description}
          </p>
          <a
            href={content.exploreAllIntegrations.cta.href}
            className="btn-primary btn-lg md:w-100"
            id="features-explore-all-integrations"
          >
            <span>{content.exploreAllIntegrations.cta.text}</span>
          </a>
        </article>{" "}
        <picture className="md:d-none">
          <source
            srcSet={`${content.exploreAllIntegrations.image.src}.webp, ${
              content.exploreAllIntegrations.image.src
            }${
              content.exploreAllIntegrations.image.hasOwnProperty("dpi")
                ? content.exploreAllIntegrations.image.dpi
                : "-2x"
            }.webp 2x`}
            type="image/webp"
          />
          <source
            srcSet={`${content.exploreAllIntegrations.image.src}.png, ${
              content.exploreAllIntegrations.image.src
            }${
              content.exploreAllIntegrations.image.hasOwnProperty("dpi")
                ? content.exploreAllIntegrations.image.dpi
                : "-2x"
            }.png 2x`}
            type="image/png"
          />
          <img
            loading="lazy"
            className="lg:-mb-11 md:mt-5 md:-mb-6 img-fit"
            src={`${content.exploreAllIntegrations.image.src}.png`}
            alt={content.exploreAllIntegrations.image.alt}
            width={content.exploreAllIntegrations.image.dimensions[0]}
            height={content.exploreAllIntegrations.image.dimensions[1]}
          />
        </picture>
      </div>
    );
  };
  return (
    <>
      <Head>
        <meta
          name="apple-itunes-app"
          content="app-id=665859578, app-argument=https://apps.apple.com/us/app/setmore-appointment-scheduling/id665859578?pid=ios_smart_banner&c=ios_campaign"
        />
      </Head>
      <Theme
        header={header}
        footer={footer}
        className="page--home features feature_active page-with-widget"
        seo={seo}
        nonce={nonce}
      >
        <AppStorage pageName="Features Root Page" />
        <Hero data={content.hero} />
        <FeaturesList
          id="core-features"
          order="one"
          data={content.featuredList.one}
          classes="lg:pb-5 md:pt-3"
        />
        <FeaturesList
          id="online-presence"
          order="two"
          data={content.featuredList.two}
          classes="lg:pb-5 md:pt-10"
        />
        <CTA
          data={content.featuredList.cta}
          imgClassName={`img-fit`}
          className="bg-light-blue-grey cta-section md:pt-10 lg:pt-15 lg:pb-10"
          colorScheme="white-grey-black"
        />
        <FeaturesList
          id="confirmations-and-reminders"
          order="three"
          data={content.featuredList.three}
          classes="lg:pb-5 md:pt-10 md:pb-5"
        />
        <FeaturesList
          id="online-scheduling"
          order="four"
          data={content.featuredList.four}
          classes="lg:pb-5 md:pt-5"
        />
        <FeaturesList
          id="calendar-sync-and-mobile-apps"
          order="five"
          data={content.featuredList.five}
          classes="lg:pb-20 md:pb-2 md:pt-5"
        />
        <Masonry
          data={content.masonry}
          className="bg-offset-top lg:mb-15 lg:-mt-9 md:mb-10 md:pt-7"
          hasTpWidget={true}
        />
        <People />
        <WideCTA data={content.wideCTA} />
        <style global jsx>{`
          .features section .card--default .btn-outline {
            background-color: white !important;
          }
          .bg-offset-top::before {
            background-color: ${colors.primitive.grey[600]};
          }
          @media (max-width: ${viewport.desktop.sm}) {
            .industry-masonry
              .card--masonry
              ul:nth-child(3)
              .client-card
              picture
              img {
              padding-right: 0px !important;
            }
          }
          @media (max-width: ${viewport.tablet.md}) {
            .industry-masonry
              .card--masonry
              ul:nth-child(3)
              .client-card
              picture
              img {
              padding-left: 28px !important;
            }
            .bg-offset-top::before {
              height: 626px;
            }
          }
        `}</style>
        <TpWidget />
      </Theme>
    </>
  );
}

export const Hero = ({ data }) => {
  const appMode = process.env.APP_MODE;
  const link = data?.cta?.href?.[appMode] || data?.cta?.href;
  return (
    <section className="container grid--default has-align-top hero--default lg:pb-10 md:pb-5">
      <article className="text-left lg:maxw-lg">
        <h1 className="text-xl lg:pt-5">{data.title}</h1>
        <p className="lg:maxw-md">{data.desc[0]}</p>
        {data.desc[1] && <p className="pt-2 lg:maxw-md">{data.desc[1]}</p>}
        <a
          href={link}
          className="btn-primary lg:mt-5 md:mt-2.5 md:mb-5"
          id="feature-page-hero-cta"
        >
          <span>{data.cta.text}</span>
        </a>
      </article>{" "}
      <picture>
        <AdaptiveCriticalImage
          src={data.images.fg.src}
          alt={data.images.fg.alt}
          mobile={data.images.fg.prefixes.mobile}
          dpi={data.images.fg.prefixes.dpi}
          className="fg-layer lg:-ml-12 img-fit"
          dimensions={data.images.fg.dimensions}
        />
        <style jsx>{`
          figure,
          picture {
            max-width: 100%;
          }
          picture :global(.fg-layer) {
            position: static !important;
            width: 750px;
          }
          @media (max-width: ${viewport.tablet.sm}) {
            picture :global(.fg-layer) {
              margin-left: 0;
              max-width: 100% !important;
              height: 100% !important;
              margin-bottom: 0 !important;
            }
          }
        `}</style>
      </picture>
    </section>
  );
};
